import {
  Grid,
  Typography,
  Divider,
  Box,
  Paper,
  Button,
  Chip,
  TablePagination,
  InputAdornment,
} from "@mui/material";
import { useIntl } from "react-intl";
import { useMemo } from "react";
import { Spinner } from "react-bootstrap";
import { numericFormatter } from "react-number-format";
import CheckIcon from "@mui/icons-material/Check";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { MuiFileInput } from "mui-file-input";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import env from "react-dotenv";
import { IconCopyCheck } from '@tabler/icons-react';

interface Empresa {
  id: number;
  clave: string;
  razon_social: string;
  nombre: string;
  logo: string;
}

interface OrdenCompraDetalle {
  id: number;
  id_inventario: number;
  equipo?: string | null;
  nombre_producto: string;
  cantidad: number;
  precio: number;
  precio_total: number;
  comentarios: string;
  estatus: number;
}

interface Proveedor {
  id: number;
  razon_social: string;
  nombre_comercial: string;
  contacto: string;
  correo: string;
  telefono: string;
}

interface OrdenCompra {
  id: number;
  id_solicitud: number;
  numero: number;
  no_serie: string;
  proyecto_area: string;
  descripcion: string;
  computo: string;
  computo_tipo?: string | null;
  folio?: string | null;
  id_solicitud_computo?: number | null;
  empresa?: Empresa | null;
  estatus: number;
  nombre_estatus: string;
  requisicion: string;
  proveedor: Proveedor;
  detalles: OrdenCompraDetalle[];
}

interface OrdenCompraPorIdProps {
  verOrdenCompra?: OrdenCompra | null;
  detallesOrdenCompraPaginados: OrdenCompraDetalle[];
  procesando?: boolean;
  page: number;
  rowsPerPage: number;
  handleChangePage: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  handleChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  procesandoOrdenCompra?: boolean;
  aceptarOrdenCompra: (datos: any) => Promise<void>;
  factura: File | null;
  handleChangeFactura: (newFile: File | null) => void;
  procesandoValidandoFactura?: boolean;
  subirPDFFactura: (datos: any) => Promise<void>;
  validarFacturaOrdenCompra: (datos: any) => Promise<void>;
}

const OrdenCompraPorId = ({
  verOrdenCompra,
  procesando,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  detallesOrdenCompraPaginados,
  procesandoOrdenCompra,
  aceptarOrdenCompra,
  factura,
  handleChangeFactura,
  procesandoValidandoFactura,
  subirPDFFactura,
  validarFacturaOrdenCompra,
}: OrdenCompraPorIdProps) => {
  const intl = useIntl();

  const totalOrdenCompraProveedor = useMemo(() => {
    return (verOrdenCompra?.detalles ?? []).reduce((acc, d) => {
      return acc + d.precio * d.cantidad;
    }, 0);
  }, [verOrdenCompra]);

  if (!verOrdenCompra) return null;

  const urlLogoEmpresa = verOrdenCompra.empresa?.logo
    ? `${env.API_URL_ASSETS}logo_empresas/${verOrdenCompra.empresa.logo}`
    : null;

  const getEstadoColor = (
    estatus_orden_compra: number
  ): "info" | "warning" | "primary" | "default" | "error" | "success" => {
    switch (estatus_orden_compra) {
      case 1:
        return "info"; // Generada
      case 2:
        return "warning"; // Enviada
      case 4:
        return "success"; // Aceptada
      case 5:
        return "success"; // Surtida
      case 6:
        return "error"; // Rechazada
      case 7:
        return "primary"; // Xml validado correctamente
      default:
        return "default";
    }
  };

  const estadosMap: { [key: number]: string } = {
    1: "Orden de compra creada",
    2: "Orden de compra enviada",
    4: "Orden aceptada",
    5: "Orden surtida",
    6: "Orden rechazada",
    7: "XML validado correctamente",
  };

  // Función helper para traducir estados
  const traducirEstado = (estado: number | null): string => {
    if (estado === null || estado === undefined) return "SIN ESTADO";
    return estadosMap[estado] ?? "SIN ESTADO";
  };

  return (
    <Box sx={{ px: 2, pb: 2, mt: 3 }}>
      <Grid container spacing={2}>
        {/* Título */}
        <Grid item xs={12}>
          <Typography variant="h4" sx={{ textAlign: "center" }}>
            Orden de compra #{verOrdenCompra.id}
          </Typography>
        </Grid>

        {/* Logo | Estatus | Botones */}
        <Grid item xs={12} md={2} sx={{ textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Logo de la empresa
          </Typography>
          {urlLogoEmpresa ? (
            <Box
              component="img"
              src={urlLogoEmpresa}
              alt="Logo de la empresa"
              sx={{ mt: 0.5, width: "auto", height: 40 }}
            />
          ) : (
            <Typography variant="body2" fontWeight="medium">
              Sin logo
            </Typography>
          )}
        </Grid>

        <Grid
          item
          xs={12}
          md={3}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Estatus de la orden de compra
          </Typography>
          <Chip
            label={traducirEstado(verOrdenCompra.estatus).toUpperCase()}
            color={getEstadoColor(verOrdenCompra.estatus)}
            sx={{ fontWeight: "bold" }}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={7}
          gap={1}
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: { xs: "center", md: "end" },
          }}
        >
          {verOrdenCompra.estatus === 2 && (
            <Button
              sx={{
                color: "#fff",
                background: "#004c93",
                fontSize: "0.75rem",
                padding: "6px 8px",
                "&:hover": { background: "#00356a" },
                "&:focus": { background: "#00356a !important" },
              }}
              variant="contained"
              disabled={procesandoOrdenCompra}
              startIcon={<IconCopyCheck stroke={2} />}
              onClick={() => aceptarOrdenCompra({ id_orden_compra: verOrdenCompra.id })}
            >
              {procesandoOrdenCompra ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  Aceptando...{" "}
                </>
              ) : (
                intl.formatMessage({ id: "general_aceptar" })
              )}
            </Button>
          )}
        </Grid>

        {/* Información general de la orden y la empresa */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: "#004c93", textAlign: "center" }}>
              Información general
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Empresa
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verOrdenCompra.empresa?.nombre ?? "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Razón social
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verOrdenCompra.empresa?.razon_social ?? "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Clave
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verOrdenCompra.empresa?.clave ?? "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Folio de requisición
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verOrdenCompra.folio ?? "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={verOrdenCompra.id_solicitud_computo ? 8 : 12}>
                <Typography variant="body2" color="text.secondary">
                  Descripción
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verOrdenCompra.descripcion || "—"}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Validar XML / Subir PDF */}
        {verOrdenCompra.estatus === 4 || verOrdenCompra.estatus === 7 ? (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ color: "#004c93" }}>
                  Validar y subir mi factura
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Para continuar el proceso, valida tu XML, sube el PDF de tu factura y da click en{" "}
                  <b style={{ color: "#004c93" }}>Subir PDF</b>, para validar la
                  información de la orden de compra y surtirla
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" justifyContent="center">
                {verOrdenCompra.estatus !== 7 && (
                  <Box
                    sx={{
                      width: { xs: "100%", md: "50%" },
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Validar XML
                    </Typography>
                    <MuiFileInput
                      value={factura}
                      onChange={handleChangeFactura}
                      inputProps={{ accept: ".xml" }}
                      hideSizeText
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CloudUploadIcon fontSize="medium" />
                          </InputAdornment>
                        ),
                        endAdornment: factura && (
                          <InputAdornment position="end">
                            <Button
                              sx={{
                                color: "#fff",
                                background: "#eb2fa5",
                                fontSize: "0.75rem",
                                padding: "6px 8px",
                                margin: "0",
                              }}
                              size="small"
                              variant="contained"
                              disabled={procesandoValidandoFactura}
                              onClick={() => {
                                const datos = {
                                  id_orden_compra: verOrdenCompra?.id,
                                  id_proveedor: verOrdenCompra?.proveedor?.id,
                                  xml_factura: factura,
                                };
                                validarFacturaOrdenCompra(datos);
                              }}
                            >
                              {procesandoValidandoFactura ? (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                  Validando...{" "}
                                </>
                              ) : (
                                intl.formatMessage({ id: "validar_xml" })
                              )}
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                      placeholder={`${intl.formatMessage({ id: "validar_xml_factura" })}`}
                    />
                  </Box>
                )}
                {verOrdenCompra.estatus === 7 && (
                  <Box
                    sx={{
                      width: { xs: "100%", md: "50%" },
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Sube tu PDF de la factura
                    </Typography>
                    <MuiFileInput
                      value={factura}
                      onChange={handleChangeFactura}
                      inputProps={{ accept: ".pdf" }}
                      hideSizeText
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PictureAsPdfIcon fontSize="medium" />
                          </InputAdornment>
                        ),
                        endAdornment: factura && (
                          <InputAdornment position="end">
                            <Button
                              sx={{
                                color: "#fff",
                                background: "#eb2fa5",
                                fontSize: "0.75rem",
                                padding: "6px 8px",
                                margin: "0",
                              }}
                              size="small"
                              variant="contained"
                              disabled={procesandoValidandoFactura}
                              onClick={() => {
                                const datos = {
                                  id_orden_compra: verOrdenCompra?.id,
                                  id_requisicion: verOrdenCompra?.id_solicitud,
                                  id_proveedor: verOrdenCompra?.proveedor?.id,
                                  pdf_factura: factura,
                                  detalles: verOrdenCompra?.detalles,
                                };
                                subirPDFFactura(datos);
                              }}
                            >
                              {procesandoValidandoFactura ? (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                  Validando...{" "}
                                </>
                              ) : (
                                intl.formatMessage({ id: "subir_pdf_factura" })
                              )}
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                      placeholder={`${intl.formatMessage({
                        id: "subir_pdf_factura_placeholder",
                      })}`}
                    />
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        ) : null}

        {/* Info del proveedor */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: "#004c93", textAlign: "center" }}>
              Datos del proveedor
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Nombre comercial
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verOrdenCompra.proveedor?.nombre_comercial || "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Razón Social
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verOrdenCompra.proveedor?.razon_social || "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Contacto
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verOrdenCompra.proveedor?.contacto || "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Correo
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verOrdenCompra.proveedor?.correo || "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Teléfono
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verOrdenCompra.proveedor?.telefono || "—"}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Productos */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: "#004c93", textAlign: "center" }}>
              Productos cotizados ({verOrdenCompra.detalles?.length ?? 0})
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {detallesOrdenCompraPaginados?.map((producto) => (
              <Box
                key={producto.id}
                sx={{ mb: 2, p: 1.5, border: "1px solid #e0e0e0", borderRadius: 1 }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Producto
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {producto?.nombre_producto ?? "—"}
                    </Typography>
                    {producto?.equipo && producto.equipo !== producto.nombre_producto && (
                      <Typography variant="caption" color="text.secondary">
                        {producto.equipo}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Typography variant="body2" color="text.secondary">
                      Cantidad
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {producto?.cantidad}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Typography variant="body2" color="text.secondary">
                      P. Unitario
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {numericFormatter(producto?.precio + "", {
                        thousandSeparator: ",",
                        decimalScale: 2,
                        fixedDecimalScale: true,
                        prefix: "$",
                      })}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Typography variant="body2" color="text.secondary">
                      Total
                    </Typography>
                    <Typography variant="body2" fontWeight="medium" sx={{ color: "#004c93" }}>
                      {numericFormatter(producto?.precio * producto?.cantidad + "", {
                        thousandSeparator: ",",
                        decimalScale: 2,
                        fixedDecimalScale: true,
                        prefix: "$",
                      })}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ))}

            {/* Total */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                mt: 1,
                p: 1,
                backgroundColor: "#e8f4fd",
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" fontWeight="bold" sx={{ mr: 1 }}>
                Total de la orden de compra:
              </Typography>
              <Typography variant="body1" fontWeight="bold" sx={{ color: "#004c93" }}>
                {numericFormatter(totalOrdenCompraProveedor + "", {
                  thousandSeparator: ",",
                  decimalScale: 2,
                  fixedDecimalScale: true,
                  prefix: "$",
                })}
              </Typography>
            </Box>

            {/* Paginación */}
            <TablePagination
              component="div"
              count={verOrdenCompra.detalles?.length ?? 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[3, 5, 10]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              sx={{
                ".MuiTablePagination-toolbar": {
                  justifyContent: "space-between",
                  padding: "8px 16px",
                },
                ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                  margin: 0,
                },
                ".MuiTablePagination-actions svg": { transform: "scaleX(-1)" },
              }}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrdenCompraPorId;
